import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Check,
  Columns as ColumnsIcon,
  Download,
  ArrowUpDown,
  ChevronRight,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  Copy,
  ListChecks,
  Trash2,
} from 'lucide-react';
import XpnsrPageHeader from './XpnsrPageHeader';
import { customParamKeys, normalizeParamKeys, paramKeyLabel } from '../reportKeys';

const DIMENSIONS = [
  ['campaign', 'Campaign'],
  ['country', 'Country'],
  ['device', 'Device'],
  ['os', 'OS'],
  ['browser', 'Browser'],
  ['stream', 'Stream'],
  ['domain', 'Domain'],
];
for (let i = 1; i <= 30; i += 1) DIMENSIONS.push(['sub' + i, 'Sub ' + i]);

// Report dimension -> Clicks-log filter key. Only these round-trip into the
// log; os / browser / sub* have no dedicated log filter so they are skipped
// when jumping (the remaining, mappable levels still pre-filter the log).
const REPORT_TO_LOG = {
  campaign: 'campaign_id',
  country: 'country',
  device: 'device',
  stream: 'stream_id',
};

const METRICS = [
  ['clicks', 'Clicks', 'int'],
  ['unique_clicks', 'Unique', 'int'],
  ['bots', 'Bots', 'int'],
  ['proxies', 'Proxies', 'int'],
  ['empty_ref', 'Empty ref.', 'int'],
  ['conversions', 'Conv.', 'int'],
  ['leads', 'Leads', 'int'],
  ['sales', 'Sales', 'int'],
  ['rejects', 'Rejected', 'int'],
  ['revenue', 'Revenue', 'money'],
  ['cost', 'Cost', 'money'],
  ['profit', 'Profit', 'money'],
  ['cr', 'CR %', 'pct'],
  ['epc', 'EPC', 'money'],
  ['uepc', 'uEPC', 'money'],
  ['roi', 'ROI %', 'pct'],
  ['cpc', 'CPC', 'money'],
  ['cpa', 'CPA', 'money'],
];

const DEFAULT_HIDDEN = ['bots', 'proxies', 'empty_ref', 'rejects', 'uepc', 'cpc', 'cpa'];
const NONE = '__none__';

function loadHidden() {
  try {
    const raw = localStorage.getItem('xpnsr.reports.hidden');
    return new Set(raw ? JSON.parse(raw) : DEFAULT_HIDDEN);
  } catch (_e) {
    return new Set(DEFAULT_HIDDEN);
  }
}

const rawVal = (row) => (row.raw_value == null ? '' : String(row.raw_value));

export default function Reports() {
  const { t } = useT();
  const navigate = useNavigate();
  const [dim1, setDim1] = useState('campaign');
  const [dim2, setDim2] = useState(NONE);
  const [dim3, setDim3] = useState(NONE);
  const [days, setDays] = useState('7');

  const [topRows, setTopRows] = useState([]);
  const [customMetrics, setCustomMetrics] = useState([]);
  const [labelFilter, setLabelFilter] = useState('none');
  const [labelMode, setLabelMode] = useState('has');
  const [manageOpen, setManageOpen] = useState(false);
  const [bwEntries, setBwEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [childrenByPath, setChildrenByPath] = useState({});

  const [sortKey, setSortKey] = useState('revenue');
  const [sortDir, setSortDir] = useState('desc');
  const [hidden, setHidden] = useState(loadHidden);
  const [paramKeys, setParamKeys] = useState([]);

  // Custom campaign parameters (a, b, Campaign_ID...) join the built-in
  // dimensions and sub1..sub15; sub keys pick up the campaign's name for them.
  useEffect(() => {
    axios
      .get(API + '/analytics/param-keys')
      .then((r) => setParamKeys(normalizeParamKeys(r.data)))
      .catch(() => setParamKeys([]));
  }, []);
  const allDims = useMemo(() => {
    const named = {};
    paramKeys.forEach((k) => { named[k.key] = k; });
    const base = DIMENSIONS.map((d) => (named[d[0]] && named[d[0]].label ? [d[0], paramKeyLabel(named[d[0]])] : d));
    return base.concat(customParamKeys(paramKeys).map((k) => [k.key, paramKeyLabel(k)]));
  }, [paramKeys]);
  const dimLabel = useMemo(() => allDims.reduce((m, d) => { m[d[0]] = d[1]; return m; }, {}), [allDims]);

  const activeDims = useMemo(
    () => [dim1, dim2, dim3].filter((d) => d && d !== NONE),
    [dim1, dim2, dim3],
  );

  const toggleCol = (k) =>
    setHidden((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      try {
        localStorage.setItem('xpnsr.reports.hidden', JSON.stringify([...n]));
      } catch (_e) {
        /* ignore */
      }
      return n;
    });

  // Build the request URL ourselves so ``filters`` serialises as repeated
  // ``filters=dim:value`` params (what FastAPI's List[str] Query expects) —
  // axios' default array serialisation uses ``filters[]=`` which the backend
  // would not pick up.
  const buildUrl = useCallback(
    (groupBy, filters) => {
      const qs = new URLSearchParams();
      qs.set('group_by', groupBy);
      qs.set('days', String(Number(days)));
      (filters || []).forEach((f) => qs.append('filters', f));
      if (labelFilter !== 'none') {
        qs.set('label', labelFilter);
        qs.set('label_mode', labelMode);
      }
      return API + '/analytics/report?' + qs.toString();
    },
    [days, labelFilter, labelMode],
  );

  // Top level reloads whenever the grouping shape or window changes; the
  // expanded children cache is dropped so stale sub-rows never linger.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setExpanded(new Set());
    setChildrenByPath({});
    axios
      .get(buildUrl(activeDims[0], []))
      .then((r) => {
        if (!cancelled) {
          setTopRows(r.data && r.data.rows ? r.data.rows : []);
          setCustomMetrics(r.data && r.data.custom_metrics ? r.data.custom_metrics : []);
        }
      })
      .catch(() => {
        if (!cancelled) setTopRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // activeDims[0] is dim1; dim2/dim3 change the tree shape too.
  }, [dim1, dim2, dim3, days, labelFilter, labelMode]);

  const loadChildren = useCallback(
    (node) => {
      const nextDim = activeDims[node.depth + 1];
      if (!nextDim) return;
      setChildrenByPath((prev) => ({ ...prev, [node.key]: { loading: true, rows: [] } }));
      axios
        .get(buildUrl(nextDim, node.filters))
        .then((r) =>
          setChildrenByPath((prev) => ({
            ...prev,
            [node.key]: { loading: false, rows: r.data && r.data.rows ? r.data.rows : [] },
          })),
        )
        .catch(() =>
          setChildrenByPath((prev) => ({
            ...prev,
            [node.key]: { loading: false, rows: [], error: true },
          })),
        );
    },
    [activeDims, buildUrl],
  );

  const toggleExpand = (node) => {
    const wasExpanded = expanded.has(node.key);
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(node.key)) n.delete(node.key);
      else n.add(node.key);
      return n;
    });
    if (!wasExpanded && !childrenByPath[node.key]) loadChildren(node);
  };

  const openInLog = (node) => {
    const qs = new URLSearchParams();
    node.filters.forEach((f) => {
      const idx = f.indexOf(':');
      if (idx < 0) return;
      const dim = f.slice(0, idx);
      const val = f.slice(idx + 1);
      const target = REPORT_TO_LOG[dim];
      if (target && val) qs.set(target, val);
    });
    // Match the report window so the log shows the same slice of time.
    qs.set('from', new Date(Date.now() - Number(days) * 86400000).toISOString());
    navigate('/clicks/log?' + qs.toString());
  };

  const markRow = async (node, listType) => {
    const dim = activeDims[node.depth];
    const value = rawVal(node.row);
    if (!dim || !value) return;
    try {
      await axios.post(API + '/bw-lists', { dimension: dim, value: value, list_type: listType });
      toast.success(listType === 'white' ? t('Added to whitelist') : t('Added to blacklist'));
    } catch (_e) {
      toast.error(t('Failed to update list'));
    }
  };
  const copyList = async (listType) => {
    try {
      const res = await axios.get(API + '/bw-lists/export', { params: { dimension: activeDims[0], list_type: listType } });
      await navigator.clipboard.writeText((res.data && res.data.text) || '');
      toast.success(t('Copied') + ' (' + ((res.data && res.data.count) || 0) + ')');
    } catch (_e) {
      toast.error(t('Failed to export list'));
    }
  };
  const loadEntries = async () => {
    try {
      const res = await axios.get(API + '/bw-lists');
      setBwEntries((res.data && res.data.entries) || []);
    } catch (_e) {
      setBwEntries([]);
    }
  };
  const deleteEntry = async (id) => {
    try {
      await axios.delete(API + '/bw-lists/' + id);
      setBwEntries((prev) => prev.filter((x) => x.id !== id));
      toast.success(t('Removed'));
    } catch (_e) {
      toast.error(t('Failed to remove'));
    }
  };
  const fmt = (v, kind) => {
    const n = Number(v) || 0;
    if (kind === 'money') return '$' + n.toFixed(2);
    if (kind === 'pct') return n.toFixed(1) + '%';
    return String(v == null ? 0 : v);
  };

  const visibleMetrics = METRICS.filter((mm) => !hidden.has(mm[0]));
  const visibleCustom = customMetrics.filter((cm) => !hidden.has('cm:' + cm.code));
  const customCell = (row, code) => {
    const c = row && row.custom && row.custom[code];
    if (!c) return '\u2014';
    if (c.formatted != null) return c.formatted;
    return c.value != null ? String(c.value) : '\u2014';
  };

  const setSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir('desc');
    }
  };

  // Depth-first flatten of the loaded tree, sorting each sibling group and
  // injecting status placeholder rows under an expanded-but-pending node.
  const flat = useMemo(() => {
    const cmp = (a, b) => {
      if (sortKey === 'name') {
        const av = String(a.name || '');
        const bv = String(b.name || '');
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const av = Number(a[sortKey]) || 0;
      const bv = Number(b[sortKey]) || 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    };
    const out = [];
    const walk = (rows, depth, parentFilters) => {
      [...rows].sort(cmp).forEach((row) => {
        const filters = parentFilters.concat(activeDims[depth] + ':' + rawVal(row));
        const key = filters.join('');
        const hasChild = depth + 1 < activeDims.length;
        out.push({ type: 'row', row, depth, key, filters, hasChild });
        if (hasChild && expanded.has(key)) {
          const kids = childrenByPath[key];
          if (!kids || kids.loading) {
            out.push({ type: 'status', depth: depth + 1, key: key + '~l', text: t('Loading…') });
          } else if (kids.error) {
            out.push({ type: 'status', depth: depth + 1, key: key + '~e', text: t('Failed to load') });
          } else if (kids.rows.length === 0) {
            out.push({
              type: 'status',
              depth: depth + 1,
              key: key + '~z',
              text: t('No data for this period.'),
            });
          } else {
            walk(kids.rows, depth + 1, filters);
          }
        }
      });
    };
    walk(topRows, 0, []);
    return out;
  }, [topRows, expanded, childrenByPath, sortKey, sortDir, activeDims, t]);

  const totals = useMemo(() => {
    const acc = {
      clicks: 0, unique_clicks: 0, bots: 0, proxies: 0, empty_ref: 0,
      conversions: 0, leads: 0, sales: 0, rejects: 0, revenue: 0, cost: 0,
    };
    topRows.forEach((r) => {
      Object.keys(acc).forEach((k) => {
        acc[k] += Number(r[k]) || 0;
      });
    });
    acc.profit = acc.revenue - acc.cost;
    acc.cr = acc.clicks ? (acc.conversions / acc.clicks) * 100 : 0;
    acc.epc = acc.clicks ? acc.revenue / acc.clicks : 0;
    acc.uepc = acc.unique_clicks ? acc.revenue / acc.unique_clicks : 0;
    acc.roi = acc.cost ? (acc.profit / acc.cost) * 100 : 0;
    acc.cpc = acc.clicks ? acc.cost / acc.clicks : 0;
    acc.cpa = acc.conversions ? acc.cost / acc.conversions : 0;
    return acc;
  }, [topRows]);

  const breadcrumb = activeDims.map((d) => t(dimLabel[d] || d)).join(' › ');

  const exportCSV = () => {
    const rowsOnly = flat.filter((n) => n.type === 'row');
    if (rowsOnly.length === 0) return;
    const cols = visibleMetrics.map((mm) => mm[0]);
    const header = [t('Group')].concat(visibleMetrics.map((mm) => t(mm[1]))).concat(visibleCustom.map((cm) => cm.name)).join(',');
    const esc = (v) => {
      const s = String(v == null ? '' : v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const body = rowsOnly
      .map((n) => {
        const label = '  '.repeat(n.depth) + (n.row.name == null ? '' : n.row.name);
        return [esc(label)].concat(cols.map((c) => esc(n.row[c]))).concat(visibleCustom.map((cm) => esc(customCell(n.row, cm.code)))).join(',');
      })
      .join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'report-' + activeDims.join('-') + '-' + days + 'd.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderDimSelect = (value, onChange, testid, withNone, disabled) => (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="w-40" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        {withNone && <SelectItem value={NONE}>{t('— no level —')}</SelectItem>}
        {allDims.map((d) => (
          <SelectItem key={d[0]} value={d[0]}>
            {t(d[1])}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="p-8 space-y-6">
      <XpnsrPageHeader
        kicker={t('Analytics')}
        title={t('Reports')}
        subtitle={`${t('Group clicks and conversions by any dimension with full metrics.')} ${t('Days are counted in the time zone set in Settings.')}`}
        actions={
          <>
            {renderDimSelect(dim1, setDim1, 'report-dimension', false, false)}
            {renderDimSelect(
              dim2,
              (v) => {
                setDim2(v);
                if (v === NONE) setDim3(NONE);
              },
              'report-dimension-2',
              true,
              false,
            )}
            {renderDimSelect(dim3, setDim3, 'report-dimension-3', true, dim2 === NONE)}
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-32" data-testid="report-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">{t('Today')}</SelectItem>
                <SelectItem value="7">{t('7 days')}</SelectItem>
                <SelectItem value="30">{t('30 days')}</SelectItem>
                <SelectItem value="90">{t('90 days')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={labelFilter} onValueChange={setLabelFilter}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('All rows')}</SelectItem>
                <SelectItem value="white">{t('Whitelisted')}</SelectItem>
                <SelectItem value="black">{t('Blacklisted')}</SelectItem>
              </SelectContent>
            </Select>
            {labelFilter !== 'none' && (
              <Select value={labelMode} onValueChange={setLabelMode}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="has">{t('Has label')}</SelectItem>
                  <SelectItem value="has_no">{t('No label')}</SelectItem>
                </SelectContent>
              </Select>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline"><Copy className="w-4 h-4 mr-2" />{t('Copy list')}</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); copyList('white'); }}>{t('Copy whitelist')}</DropdownMenuItem>
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); copyList('black'); }}>{t('Copy blacklist')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={() => { setManageOpen(true); loadEntries(); }}>
              <ListChecks className="w-4 h-4 mr-2" />{t('Lists')}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <ColumnsIcon className="w-4 h-4 mr-2" />
                  {t('Columns')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
                <DropdownMenuLabel>{t('Columns')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {METRICS.map((mm) => (
                  <DropdownMenuItem
                    key={mm[0]}
                    onSelect={(e) => {
                      e.preventDefault();
                      toggleCol(mm[0]);
                    }}
                  >
                    <Check
                      className={
                        'mr-2 h-4 w-4 ' + (hidden.has(mm[0]) ? 'opacity-0' : 'opacity-100')
                      }
                    />
                    {t(mm[1])}
                  </DropdownMenuItem>
                ))}
                {customMetrics.length > 0 && <DropdownMenuSeparator />}
                {customMetrics.map((cm) => (
                  <DropdownMenuItem
                    key={'cm:' + cm.code}
                    onSelect={(e) => {
                      e.preventDefault();
                      toggleCol('cm:' + cm.code);
                    }}
                  >
                    <Check className={'mr-2 h-4 w-4 ' + (hidden.has('cm:' + cm.code) ? 'opacity-0' : 'opacity-100')} />
                    {cm.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={exportCSV}>
              <Download className="w-4 h-4 mr-2" />
              {t('Export CSV')}
            </Button>
          </>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('Report')}</CardTitle>
          <CardDescription>
            {t('Grouped by')} {breadcrumb}
            {activeDims.length > 1 && (
              <span className="ml-2 text-muted-foreground">
                · {t('Click a row to drill in')}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table className="xp-sticky-first">
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => setSort('name')}
                >
                  <span className="inline-flex items-center gap-1">
                    {t('Group')}
                    <ArrowUpDown className="w-3 h-3 opacity-60" />
                  </span>
                </TableHead>
                {visibleMetrics.map((mm) => (
                  <TableHead
                    key={mm[0]}
                    className="cursor-pointer select-none text-right"
                    onClick={() => setSort(mm[0])}
                  >
                    <span className="inline-flex items-center gap-1 justify-end">
                      {t(mm[1])}
                      <ArrowUpDown className="w-3 h-3 opacity-60" />
                    </span>
                  </TableHead>
                ))}
                {visibleCustom.map((cm) => (
                  <TableHead key={'cm:' + cm.code} className="text-right">
                    <span className="inline-flex items-center gap-1 justify-end">{cm.name}</span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {topRows.length > 0 && (
                <TableRow className="bg-surface-2 font-semibold">
                  <TableCell>{t('Total')}</TableCell>
                  {visibleMetrics.map((mm) => (
                    <TableCell key={mm[0]} className="text-right tabular-nums">
                      {fmt(totals[mm[0]], mm[2])}
                    </TableCell>
                  ))}
                  {visibleCustom.map((cm) => (
                    <TableCell key={'cm:' + cm.code} className="text-right tabular-nums text-muted-foreground">—</TableCell>
                  ))}
                </TableRow>
              )}
              {flat.map((n) =>
                n.type === 'status' ? (
                  <TableRow key={n.key}>
                    <TableCell
                      colSpan={visibleMetrics.length + visibleCustom.length + 1}
                      className="text-muted-foreground text-sm"
                    >
                      <span style={{ paddingLeft: n.depth * 20 }}>{n.text}</span>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={n.key} data-testid={'report-row-' + n.depth}>
                    <TableCell className="font-medium">
                      <div
                        className="flex items-center gap-1"
                        style={{ paddingLeft: n.depth * 20 }}
                      >
                        {n.hasChild ? (
                          <button
                            type="button"
                            onClick={() => toggleExpand(n)}
                            aria-label={expanded.has(n.key) ? t('Collapse') : t('Expand')}
                            className="p-0.5 rounded hover:bg-surface-2"
                            data-testid={'report-expand-' + n.depth}
                          >
                            <ChevronRight
                              className={
                                'w-4 h-4 transition-transform ' +
                                (expanded.has(n.key) ? 'rotate-90' : '')
                              }
                            />
                          </button>
                        ) : (
                          <span style={{ width: 20, display: 'inline-block' }} />
                        )}
                        <span>{n.row.name}</span>
                        <button
                          type="button"
                          onClick={() => openInLog(n)}
                          title={t('Open in clicks log')}
                          aria-label={t('Open in clicks log')}
                          className="ml-1 opacity-40 hover:opacity-100"
                          data-testid="report-open-log"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => markRow(n, 'white')}
                          title={t('Whitelist')}
                          aria-label={t('Whitelist')}
                          className="ml-1 opacity-40 hover:opacity-100 hover:text-primary"
                        >
                          <ThumbsUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => markRow(n, 'black')}
                          title={t('Blacklist')}
                          aria-label={t('Blacklist')}
                          className="ml-1 opacity-40 hover:opacity-100 hover:text-destructive"
                        >
                          <ThumbsDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </TableCell>
                    {visibleMetrics.map((mm) => (
                      <TableCell key={mm[0]} className="text-right tabular-nums">
                        {fmt(n.row[mm[0]], mm[2])}
                      </TableCell>
                    ))}
                    {visibleCustom.map((cm) => (
                      <TableCell key={'cm:' + cm.code} className="text-right tabular-nums">
                        {customCell(n.row, cm.code)}
                      </TableCell>
                    ))}
                  </TableRow>
                ),
              )}
              {topRows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={visibleMetrics.length + visibleCustom.length + 1}
                    className="text-center text-muted-foreground py-8"
                  >
                    {loading ? t('Loading…') : t('No data for this period.')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('Black / white lists')}</DialogTitle>
            <DialogDescription>{t('Marked values are reusable as report filters and copyable for ad networks.')}</DialogDescription>
          </DialogHeader>
          {bwEntries.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">{t('No list entries yet. Use the thumbs buttons on report rows.')}</p>
          ) : (
            <div className="space-y-1">
              {bwEntries.map((en) => (
                <div key={en.id} className="flex items-center justify-between gap-2 border border-border rounded px-2 py-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={'text-xs px-1.5 py-0.5 rounded ' + (en.list_type === 'white' ? 'bg-primary/15 text-primary' : 'bg-destructive/15 text-destructive')}>
                      {en.list_type === 'white' ? t('White') : t('Black')}
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0">{t(dimLabel[en.dimension] || en.dimension)}</span>
                    <span className="text-sm font-mono truncate">{en.value}</span>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" aria-label={t('Delete list entry')} title={t('Delete list entry')} onClick={() => deleteEntry(en.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
